import React from "react";
import { Box, Tooltip, Modal, Typography } from "@mui/material";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import CloseIcon from "@mui/icons-material/Close";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Skeleton from "@mui/material/Skeleton";
import 'overlayscrollbars/overlayscrollbars.css';
import { OverlayScrollbarsComponent } from "overlayscrollbars-react";
import moment from "moment";
import { useSelector } from "react-redux";
import { fetchDataSimple } from "../../../../services/data.service";
import { replaceMultiText } from "../../../../app/util";

const ACCENT = "#01A4EF";
const ACCENT_DEEP = "#00415F";
const LINE = "#e2e6ea";
const ZEBRA = "#f6f8fa";

const MarketCensusButton = () => {
    const { app_state } = useSelector((state) => state.app.current_state);
    const [open, setOpen] = React.useState(false);
    const [jsonData, setJsonData] = React.useState(null);
    const [hasError, setHasError] = React.useState(false);

    const handleClose = () => setOpen(false);

    const handleClick = () => {
        setOpen(true);
        setJsonData(null);
        setHasError(false);

        if (!app_state.selected_market || !app_state.selected_market.market_id) {
            setHasError(true);
            return;
        }

        try {
            const correction = {
                "{mid}": app_state.selected_market.market_id,
                "{asOfDate}": moment(app_state.as_of_date).format("YYYY-MM-DD"),
            };
            const request = {
                "method": "GET",
            };
            let api = "rms/markets/census-info/{mid}/?asOfDate={asOfDate}";
            api = replaceMultiText(api, correction);
            fetchDataSimple({ api, request })
                .then((response) => {
                    if (response && response.status === "success" && Array.isArray(response.data)) {
                        setJsonData(response);
                    } else {
                        setHasError(true);
                    }
                })
                .catch((error) => {
                    console.log(error);
                    setHasError(true);
                });
        } catch (error) {
            console.log(error);
            setHasError(true);
        }
    };

    let hotels = (jsonData && Array.isArray(jsonData.data)) ? jsonData.data : [];
    let marketCapacity = hotels.reduce((total, hotel) => total + (hotel.room_count || 0), 0);
    let responseAsOfDate = jsonData && jsonData.meta && jsonData.meta.as_of_date;
    let asOfDateLabel = responseAsOfDate ? moment(responseAsOfDate).format('MMM D, YYYY') : '';
    let isEmpty = jsonData !== null && hotels.length === 0;
    let showUnavailable = hasError || isEmpty;
    let isLoading = jsonData === null && !hasError;
    let marketLabel = app_state.selected_market && app_state.selected_market.label;

    return (
        <>
            <Box className="switch-box market-census-btn">
                <Box className="data-level-label">Market Census</Box>
                <Tooltip title="Market Census" followCursor>
                    <Box onClick={handleClick} sx={{ height: 38, display: "flex", alignItems: "center", justifyContent: "center" }}>
                        <Box
                            sx={{
                                cursor: "pointer",
                                width: 30,
                                height: 30,
                                borderRadius: "50%",
                                boxShadow: "inset 0 0 0 1px #B2B2B2",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                            }}
                        >
                            <ApartmentOutlinedIcon sx={{ width: 17, height: 17, color: "#606060" }} />
                        </Box>
                    </Box>
                </Tooltip>
            </Box>

            <Modal
                open={open}
                aria-labelledby="modal-modal-title"
                aria-describedby="modal-modal-description"
            >
                <Box sx={{
                    position: 'absolute',
                    top: '40%',
                    left: '50%',
                    transform: 'translate(-50%, -50%)',
                    width: '680px', maxHeight: '600px',
                    bgcolor: '#fff',
                    boxShadow: '0px 10px 28px rgba(19,42,58,0.22)',
                    borderRadius: '10px',
                    overflow: 'hidden',
                    outline: 'none',
                    display: 'flex',
                    flexDirection: 'column',
                }}>
                    <Box sx={{
                        display: "flex", justifyContent: "space-between", alignItems: "flex-start",
                        px: 2.5, py: 1.8, borderBottom: `1px solid ${LINE}`,
                    }}>
                        <Box>
                            <Typography sx={{ fontSize: "10px", fontWeight: 700, letterSpacing: "0.07em", color: ACCENT, textTransform: "uppercase" }}>
                                Market Census
                            </Typography>
                            <Typography id="modal-modal-title" sx={{ fontSize: "16px", fontWeight: 700, color: "#23282c", mt: 0.25 }}>
                                {marketLabel}
                            </Typography>
                        </Box>
                        <CloseIcon onClick={handleClose} sx={{ cursor: "pointer", color: "#9aa1a7", mt: 0.25 }} />
                    </Box>

                    <Box sx={{ display: "flex", borderBottom: `1px solid ${LINE}` }}>
                        <Box sx={{ flex: 1, px: 2.5, py: 1.6, borderRight: `1px solid ${LINE}` }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", color: "#63696e", textTransform: "uppercase" }}>
                                Market Capacity
                            </Typography>
                            {isLoading ? (
                                <Skeleton width="60%" height={28} sx={{ mt: 0.5 }} />
                            ) : (
                                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: showUnavailable ? "#9aa1a7" : ACCENT, mt: 0.5 }}>
                                    {showUnavailable ? "N/A" : marketCapacity.toLocaleString()}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, px: 2.5, py: 1.6, borderRight: `1px solid ${LINE}` }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", color: "#63696e", textTransform: "uppercase" }}>
                                Properties
                            </Typography>
                            {isLoading ? (
                                <Skeleton width="40%" height={28} sx={{ mt: 0.5 }} />
                            ) : (
                                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: showUnavailable ? "#9aa1a7" : "#23282c", mt: 0.5 }}>
                                    {showUnavailable ? "N/A" : hotels.length}
                                </Typography>
                            )}
                        </Box>
                        <Box sx={{ flex: 1, px: 2.5, py: 1.6 }}>
                            <Typography sx={{ fontSize: "11px", fontWeight: 700, letterSpacing: "0.04em", color: "#63696e", textTransform: "uppercase" }}>
                                As Of Date
                            </Typography>
                            {isLoading ? (
                                <Skeleton width="70%" height={28} sx={{ mt: 0.5 }} />
                            ) : (
                                <Typography sx={{ fontSize: "20px", fontWeight: 700, color: showUnavailable ? "#9aa1a7" : ACCENT_DEEP, mt: 0.5 }}>
                                    {showUnavailable ? "N/A" : asOfDateLabel}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    <OverlayScrollbarsComponent
                        className="market-census-scrollbar"
                        element="div"
                        options={{
                            scrollbars: { autoHide: 'leave', autoHideDelay: 100 },
                            overflow: { x: 'hidden', y: 'scroll' },
                        }}
                        style={{ maxHeight: 460 }}
                        defer
                    >
                        <Table stickyHeader sx={{ tableLayout: 'fixed', width: '100%', '& td, & th': { py: '6px', px: 2.5 } }}>
                            <TableHead>
                                <TableRow>
                                    <TableCell sx={{ width: "70%", background: "#e9edf0", color: "#3a4147", fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", borderBottom: `1px solid ${LINE}` }}>
                                        Property
                                    </TableCell>
                                    <TableCell align="right" sx={{ width: "30%", background: "#e9edf0", color: "#3a4147", fontSize: "11px", fontWeight: 700, letterSpacing: "0.03em", textTransform: "uppercase", borderBottom: `1px solid ${LINE}` }}>
                                        Rooms
                                    </TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {isLoading && [1, 2, 3, 4, 5].map((i) => (
                                    <TableRow key={i}>
                                        <TableCell sx={{ borderBottom: `1px solid ${LINE}` }}><Skeleton width="70%" /></TableCell>
                                        <TableCell align="right" sx={{ borderBottom: `1px solid ${LINE}` }}><Skeleton width="40%" sx={{ ml: "auto" }} /></TableCell>
                                    </TableRow>
                                ))}
                                {!isLoading && showUnavailable && (
                                    <TableRow>
                                        <TableCell colSpan={2} sx={{ height: "260px", verticalAlign: "middle", textAlign: "center", fontSize: "14px", color: "#63696e", lineHeight: 1.6 }}>
                                            Census data is currently unavailable, please contact&nbsp;
                                            <Box component="span" sx={{ color: ACCENT_DEEP, fontWeight: 600 }}>success@lodgiq.com</Box>
                                            &nbsp;for further details.
                                        </TableCell>
                                    </TableRow>
                                )}
                                {!isLoading && !showUnavailable && hotels.map((item, index) => (
                                    <TableRow key={index} sx={{ background: index % 2 === 1 ? ZEBRA : "#fff" }}>
                                        <TableCell sx={{
                                            fontSize: "13px", color: "#23282c", textTransform: "capitalize",
                                            overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                                            borderBottom: `1px solid ${LINE}`,
                                        }}>
                                            {item.name && item.name.toLowerCase()}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontSize: "13px", fontWeight: 600, color: ACCENT_DEEP, borderBottom: `1px solid ${LINE}` }}>
                                            {(item.room_count || 0).toLocaleString()}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </OverlayScrollbarsComponent>
                </Box>
            </Modal>
        </>
    )
}

export default MarketCensusButton
